import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BlogCard from './components/BlogCard';
import { supabase, Post } from '@/lib/supabase';
import JsonLd from '@/components/base/JsonLd';

const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://grainn-office.com';

const blogCategories = [
  { id: 'all', label: 'すべて' },
  { id: 'web', label: 'WEB制作' },
  { id: 'sns', label: 'SNS運用' },
  { id: 'seo', label: 'SEO・MEO' },
  { id: 'meo', label: 'MEO対策' },
  { id: 'marketing', label: 'マーケティング' },
  { id: 'ai', label: 'AI活用' },
];

const blogListJsonLd = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'CollectionPage',
      '@id': `${siteUrl}/blog#webpage`,
      url: `${siteUrl}/blog`,
      name: 'ブログ｜WEB制作・SNS運用・MEO対策のノウハウ | GRAINN',
      description: 'GRAINNのブログでは、WEBサイト制作・SNS運用代行・MEO対策・デジタルマーケティングに関する最新情報やノウハウを発信しています。',
      inLanguage: 'ja',
      isPartOf: { '@id': `${siteUrl}/#website` },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'ホーム', item: `${siteUrl}/` },
          { '@type': 'ListItem', position: 2, name: 'ブログ', item: `${siteUrl}/blog` },
        ],
      },
    },
    {
      '@type': 'Blog',
      '@id': `${siteUrl}/blog#blog`,
      name: 'GRAINNブログ',
      description: 'WEBサイト制作・SNS運用代行・MEO対策・AI活用など、ビジネスに役立つ最新情報を発信。',
      url: `${siteUrl}/blog`,
      publisher: {
        '@type': 'Organization',
        name: 'GRAINN',
        url: `${siteUrl}/`,
      },
      inLanguage: 'ja',
    },
  ],
};

function postToMock(post: Post) {
  const dateStr = post.published_at ?? post.created_at ?? '';
  return {
    slug: post.slug,
    title: post.title,
    excerpt: post.excerpt,
    content: post.content,
    category: post.category,
    tags: post.tags,
    publishedAt: dateStr ? dateStr.split('T')[0] : '',
    readingTime: post.reading_time,
    coverImage: post.cover_image,
    author: {
      name: post.author_name,
      role: post.author_role,
      avatar: post.author_avatar,
    },
  };
}

export default function BlogPage() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      setLoading(true);
      const query = supabase
        .from('posts')
        .select('*')
        .eq('published', true)
        .order('published_at', { ascending: false });

      const { data } = await query;
      setPosts(data ?? []);
      setLoading(false);
    };
    fetchPosts();
  }, []);

  const filtered = activeCategory === 'all'
    ? posts
    : posts.filter((p) => p.category === activeCategory);

  const featured = filtered[0] ? postToMock(filtered[0]) : null;
  const rest = filtered.slice(1).map(postToMock);

  return (
    <main className="min-h-screen bg-white">
      <title>ブログ｜WEB制作・SNS運用・MEO対策のノウハウ | GRAINN</title>
      <meta name="description" content="GRAINNのブログでは、WEBサイト制作・SNS運用代行・MEO対策・デジタルマーケティングに関する最新情報やノウハウを発信しています。大阪のWEB制作会社GRAINNが運営。" />
      <meta name="keywords" content="WEB制作,SNS運用,MEO対策,デジタルマーケティング,ブログ,GRAINN" />
      <link rel="canonical" href={`${siteUrl}/blog`} />
      <meta property="og:title" content="ブログ｜WEB制作・SNS運用・MEO対策のノウハウ | GRAINN" />
      <meta property="og:description" content="GRAINNのブログでは、WEBサイト制作・SNS運用代行・MEO対策・デジタルマーケティングに関する最新情報やノウハウを発信しています。" />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={`${siteUrl}/blog`} />
      <meta property="og:locale" content="ja_JP" />
      <meta property="og:site_name" content="GRAINN" />
      <JsonLd data={blogListJsonLd} id="blog-list" />

      <Navbar />

      {/* Hero */}
      <section
        className="pt-36 pb-20 relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #020B18 0%, #061428 60%, #0A1E3D 100%)' }}
      >
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: 'linear-gradient(#1E6FFF 1px, transparent 1px), linear-gradient(90deg, #1E6FFF 1px, transparent 1px)',
            backgroundSize: '80px 80px',
          }}
        />
        <div
          className="absolute top-0 right-1/4 w-96 h-96 rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(30,111,255,0.12) 0%, transparent 70%)', filter: 'blur(40px)' }}
        />
        <div className="max-w-7xl mx-auto px-6 md:px-10 relative z-10">
          <p className="text-[#4DA3FF] text-xs tracking-[0.4em] mb-5 uppercase flex items-center gap-3">
            <span className="inline-block w-6 h-px bg-[#1E6FFF]" />
            Blog
          </p>
          <h1 className="text-4xl md:text-5xl font-bold text-white tracking-tight mb-5">
            ブログ
          </h1>
          <p className="text-white/50 text-base leading-relaxed max-w-xl">
            WEB制作・SNS運用・MEO対策・AI活用など、<br />
            ビジネスに役立つ最新情報を発信しています。
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="sticky top-20 z-30 bg-white border-b border-[#E8F0FF]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex items-center gap-2 overflow-x-auto py-4 scrollbar-hide">
            {blogCategories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`whitespace-nowrap px-5 py-2 rounded-full text-sm font-medium transition-all cursor-pointer ${
                  activeCategory === cat.id
                    ? 'text-white'
                    : 'text-[#666] bg-[#F5F7FA] hover:bg-[#E8F0FF] hover:text-[#1E6FFF]'
                }`}
                style={activeCategory === cat.id ? { background: 'linear-gradient(90deg, #1E6FFF, #0099FF)' } : {}}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Posts */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          {loading ? (
            <div className="text-center py-20">
              <div className="w-8 h-8 border-2 border-[#1E6FFF] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
              <p className="text-xs text-[#999]">読み込み中...</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-[#F5F7FA]">
                <i className="ri-article-line text-2xl text-[#ccc]" />
              </div>
              <p className="text-[#999] text-sm">このカテゴリの記事はまだありません</p>
            </div>
          ) : (
            <>
              {featured && (
                <div className="mb-10">
                  <BlogCard post={featured} featured />
                </div>
              )}
              {rest.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                  {rest.map((post) => (
                    <BlogCard key={post.slug} post={post} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-[#F8FAFF]">
        <div className="max-w-3xl mx-auto px-6 md:px-10 text-center">
          <p className="text-[#1E6FFF] text-xs tracking-[0.3em] mb-4 uppercase">/ Contact</p>
          <h2 className="text-2xl md:text-3xl font-bold text-[#0A1628] mb-4">
            WEB・SNS・MEOのご相談はお気軽に
          </h2>
          <p className="text-[#666] text-sm leading-relaxed mb-8">
            初回相談は無料です。ご予算・スケジュール・ご要望など、<br className="hidden sm:block" />
            まずはお気軽にお話しください。
          </p>
          <a
            href="/contact"
            className="inline-flex items-center gap-2 text-white px-10 py-4 rounded-full text-sm font-bold tracking-widest transition-all hover:opacity-90 hover:scale-105 whitespace-nowrap"
            style={{ background: 'linear-gradient(90deg, #1E6FFF 0%, #0099FF 100%)' }}
          >
            無料相談を申し込む
            <i className="ri-arrow-right-line" />
          </a>
        </div>
      </section>

      <Footer />
    </main>
  );
}
